import type { BanDich } from '../../types';
import type { viNktvSuDungAi } from './su-dung-ai.vi';

export const enNktvSuDungAi: Pick<BanDich, keyof typeof viNktvSuDungAi> = {
  nktvSuDungAi: {
    tieuDe: 'AI usage log',
    cotTenant: 'Tenant',
    cotGiaVon: 'Provider cost',
    tatCaTenant: 'All tenants',
    tab: {
      tongQuan: 'Overview',
      trace: 'By trace',
      luotGoi: 'Model calls',
      luong: 'By business object',
    },
    loc: {
      moHinh: 'Model',
      nhaCungCap: 'Provider',
      service: 'Service',
      tinhNang: 'Feature',
      nguoiDung: 'User',
      donVi: 'Unit',
      trangThai: 'Pricing status',
      trangThaiDaTinh: 'Priced',
      trangThaiChuaCoGia: 'No price yet',
      coCanhBao: 'Token warning',
      coCanhBaoCo: 'With warning',
      coCanhBaoKhong: 'Without warning',
      loiGoiY: 'Could not load filter suggestions — some values may be missing from the lists.',
    },
    danhSach: {
      sapXep: 'Sort by',
      moiNhat: 'Newest',
      tonNhat: 'Most tokens',
      xepHangMotTrang:
        '"Most tokens" is a single-page ranking — it cannot load more. To browse all data, switch back to "Newest".',
      het: 'Showing all {{so}} rows that match this filter.',
      rongLoc: 'No rows match the filter',
    },
    trace: {
      bang: 'Processing traces',
      goiY: 'Each row is one complete processing trace — one question, one document scan — including every model call inside it. Click a row to see each step, its tokens and its cost.',
      cotBatDau: 'Started',
      cotChuoi: 'Trace',
      cotNguoi: 'Performed by',
      cotService: 'Services',
      cotBuoc: 'Steps',
      cotMoHinh: 'Models',
      cotToken: 'Tokens',
      cotChiPhi: 'Cost (sale price)',
      cotTietKiem: 'Cache savings',
      cotKeoDai: 'Duration',
      cotMo: 'Open',
      soBuoc: '{{so}} steps',
      soLoi: '{{so}} errors',
      soLuotLlm: '{{so}} AI calls',
      hoiChatbot: 'Asked chatbot {{ten}}',
      tieuDeCoAi: 'Trace with {{so}} AI calls',
      maTraCuuNgan: 'Trace ID {{ma}}…',
      moChuoi: 'Open the trace to see each step',
      rongTieuDe: 'No processing traces',
      rongMoTa: 'There are no traces in this time range. Try widening the time range above.',
    },
    luotGoi: {
      bang: 'Model calls',
      cotThoiGian: 'Time',
      cotMoHinh: 'Model',
      cotTinhNang: 'Feature',
      cotNguoi: 'User / object',
      cotToken: 'Tokens',
      cotChiPhi: 'Cost (sale price)',
      cotTietKiem: 'Cache savings',
      cotDoTre: 'Latency',
      cotTinCay: 'Reliability',
      cotChuoi: 'Trace',
      xemChuoi: 'View trace',
      rongTieuDe: 'No model calls',
      rongMoTa: 'There are no calls in this time range. Try widening the time range above.',
    },
    luong: {
      bang: 'Business objects by tokens',
      cotDoiTuong: 'Object',
      cotKhoang: 'From → to',
      cotQuyMo: 'Scale',
      cotNguoi: 'Users',
      cotService: 'Services',
      cotToken: 'Tokens',
      cotChiPhi: 'Cost (sale price)',
      loai: 'Object type',
      loaiTatCa: 'All',
      soTrace: '{{so}} traces',
      cacTrace: 'Processing traces of this object — click an ID to see each step:',
      khongCoTrace: 'The aggregated record does not include the list of trace IDs.',
      motTrang: 'Showing {{so}} objects, sorted by total tokens descending. This is a single-page ranking; it cannot load more.',
      rongTieuDe: 'No business objects',
      rongMoTa: 'No business object was processed in this range. Try widening the time range above.',
    },
    chuoi: {
      tieuDe: 'Timeline of a processing trace',
      goiMoHinh: 'Model call',
      luotGoiAi: 'AI calls',
      batDauSau: 'starts after {{lech}}',
      moChiTiet: 'Click to see this step’s details',
      goiYMo: 'Click a step to see its model, cost and token breakdown.',
      cotBuoc: 'Step',
      cotToken: 'Tokens',
      luc: 'Time',
      dichVu: 'Service',
      doiTuong: 'Object',
      moHinh: 'Model',
      chiPhi: 'Cost (sale price)',
      tietKiem: 'Cache savings',
      bocTach: 'Seven-type token breakdown',
      maTraCuu: 'Trace ID (trace_id)',
      keoDai: 'Duration',
      soBuoc: 'Steps',
      tongToken: 'Total tokens',
      lech: '{{lech}} after start',
      doTre: 'latency {{doTre}}',
      khongKhaiDoTre: 'this step did not record a latency',
      thanhToken: 'Step tokens: {{so}}',
      biCat:
        'The trace is longer than the 200-step limit, so the list below is truncated — the last steps are not shown, and the total tokens only count the loaded part.',
      chuGiaiThuong: 'Business step',
      chuGiaiLlm: 'Model call',
      chuGiaiLoi: 'Failed step',
      chuGiaiToken: 'Step tokens (relative to the step with the most tokens)',
      chuGiaiVach: 'Thin mark = the step did not record a latency, only its time.',
      rongTieuDe: 'The trace has no steps',
      rongMoTa: 'No record with this trace ID is within the scope you can view.',
    },
    token: {
      thanhNhan: 'Share of the seven token types in this call',
      dau_vao: 'Input',
      dau_vao_anh: 'Image input',
      dau_vao_tool: 'Tool input',
      dau_vao_dem: 'Cache read',
      ghi_dem: 'Cache write',
      dau_ra: 'Output',
      suy_luan: 'Reasoning',
      congBay: 'Sum of seven types:',
      tongBao: 'Total reported by the system:',
      khongBao: 'The provider did not report this type — not the same as zero.',
      soKhongBao: '{{so}} types not reported',
      lechPhanHoach:
        'The sum of the seven types differs from the recorded total by {{lech}} tokens. They must add up exactly to the total, so this is a token normalizer bug — do not use this row for final figures.',
    },
    co: {
      chuaCoGia: 'No price yet',
      chuaCoGiaMoTa:
        'This model is not in the platform’s model pricing yet, so the cost is missing that part. The token count is still correct.',
      khaiGia: 'Open pricing',
      khaiGiaMoTa: 'Open model pricing to add a price for model {{moHinh}}',
      thieuTyGia: 'Missing exchange rate',
      thieuTyGiaMoTa:
        'The model has a price but there is no exchange rate to convert it to the reporting currency for the call’s date, so the cost is left blank. The token count is still correct. Exchange rates are entered by the platform administrator in model pricing.',
      xemTyGia: 'View exchange rates',
      xemTyGiaMoTa: 'Open model pricing, where exchange rates are entered',
      canhBao: 'Conversion warnings: {{so}}',
      canhBaoMoTa:
        'The token normalizer raised a warning about this call’s own token conversion, so the figures are not reliable enough to finalize. To fix: ask the engineering team to correct the token normalizer.',
      uocTinh: 'estimated price',
      uocTinhMoTa: 'Cost calculated from an estimated price, not a declared one.',
      khongDangTin: 'Not reliable yet',
      khongDangTinMoTa:
        'At least one call in the trace carries a token conversion warning, so the trace total is not used for final figures.',
      tietKiemAm:
        'Negative value: reading back from the cache cost more than a fresh call. This is the real result of the calculation, not a display error.',
    },
    tongQuan: {
      khongLuotTieuDe: 'No model calls',
      khongLuotMoTa:
        'No model call was traced in the selected period. Try widening the time range, or check whether the services in use send AI call traces.',
      soLuot: 'Model calls',
      soLuotPhu: 'Across {{trace}} processing traces.',
      tongToken: 'Total tokens',
      tongTokenPhu: 'Sum of all seven token types.',
      chiPhi: 'Cost (sale price)',
      chiPhiPhu: 'Amount fixed at write time, using the price list in effect.',
      chiPhiVon: 'Provider cost: {{tien}}.',
      chiPhiChuaDu: '{{so}} calls have no price yet — this amount is incomplete.',
      soMoHinh: 'Models',
      soMoHinhPhu: 'Distinct models called in the period.',
    },
    bieuDo: {
      tokenTieuDe: 'Tokens over time, split into seven types',
      tokenMoTa:
        'The seven token types do not overlap: every token belongs to exactly one type, so the seven parts add up exactly to the total.',
      tokenNhan: 'Stacked columns: tokens over time, split into seven types',
      tokenKhop: 'The seven parts add up to {{bay}} tokens and audit-service recorded a total of {{tong}} — they match.',
      tokenLech:
        'The seven parts add up to {{bay}} tokens but audit-service recorded a total of {{tong}}, and {{moc}} time buckets are off. The seven token types no longer add up to the total: the fault is in the token normalizer, not in the chart. See each call’s warnings in the Model calls tab.',
      tongToken: 'Total tokens',
      soTokenThe: '{{so}} tokens',
      soLuot: 'Calls',
      chiPhi: 'Cost',
      chiPhiTieuDe: 'Cost over time',
      chiPhiMoTa: 'Sale price, using the price list in effect at write time.',
      chiPhiNhan: 'Columns: cost over time',
      chiPhiGhiChu: 'Amounts are fixed at write time and never change, even if the price list changes later.',
      chiPhiChuaDu:
        '{{so}} calls in the period have no price yet and are not counted in these columns — the actual total cost is higher than the chart shows.',
      tietKiemThe: 'Savings',
      tietKiemTieuDe: 'Cache savings',
      tietKiemMoTa: 'Amount saved by reading back from the cache, minus the amount paid to write to the cache.',
      tietKiemNhan: 'Columns: cache savings over time, may be negative',
      tietKiemAm:
        'A negative number is not an error: cache writes currently cost more than the savings, i.e. writes were paid for but not reused enough to break even. Negative values are shown with their sign, not clamped to 0.',
      tietKiemPhiLuu: 'Hourly cache storage fees are not included — they are outside the formula.',
    },
    phanRa: {
      tieuDe: 'Breakdown by group',
      moTa: 'The same columns for every grouping; each grouping takes the 50 rows with the most tokens.',
      nhom: 'Group by',
      bang: 'Token and cost breakdown by group',
      theo_tenant: 'Tenant',
      theo_mo_hinh: 'Model',
      theo_service: 'Service',
      theo_tinh_nang: 'Feature',
      theo_don_vi: 'Unit',
      theo_nguoi_dung: 'User',
      rongTieuDe: 'No data in this period',
      rongMoTa: 'No record falls in the selected range. Try widening the time range.',
      cotKhoa: 'Group',
      cotSoLuot: 'Calls',
      cotTongToken: 'Total tokens',
      cotChiPhi: 'Cost',
      cotDoTre: 'Avg. latency',
      doTreTrong: 'No call recorded a latency — not the same as 0 ms',
      cotTyLeLoi: 'Error rate',
      tyLeLoiMoTa: '{{loi}} failed out of {{tong}} calls',
      chuaCoGia: '{{so}} unpriced',
      chuaCoGiaMoTa:
        'Some calls could not be priced because of a missing model price or exchange rate — this row’s amount is incomplete',
      coCanhBao: 'Warnings: {{so}}',
      coCanhBaoMoTa: 'Some calls carry a token conversion warning — this row’s token count is not reliable yet',
    },
  },
};
